import { useState, useCallback } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import {
  BookOpen,
  ChevronRight,
  Volume2,
  MessageCircle,
  User,
  CheckCircle,
} from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useSoundEffects } from '../../../hooks/cabin/useSoundEffects';
import { useConfetti } from '../../../hooks/cabin/useConfetti';

/**
 * StoryMode — Scene-by-scene story exercise
 *
 * phase.content schema:
 * {
 *   scenes: [{ id, narration_tr, narration_en, speaker, image_url, audio_url,
 *              choices: [{ text_tr, text_en, next_scene_id, consequence_tr, consequence_en, points }] }],
 *   outcomes: [{ min_points, max_points, title_tr, title_en, desc_tr, desc_en }]
 * }
 */
export default function StoryMode({
  phase,
  onPhaseComplete,
  'data-testid': testId = 'story-mode',
}) {
  const { t, lang } = useLanguage();
  const { play } = useSoundEffects();
  const { fire } = useConfetti();
  const shouldReduce = useReducedMotion();

  const scenes = phase?.content?.scenes || [];
  const outcomes = phase?.content?.outcomes || [];
  const maxXP = phase?.xp_in_phase || 0;

  const [currentSceneId, setCurrentSceneId] = useState(scenes[0]?.id || null);
  const [totalPoints, setTotalPoints] = useState(0);
  const [sceneHistory, setSceneHistory] = useState([scenes[0]?.id].filter(Boolean));
  const [showOutcome, setShowOutcome] = useState(false);
  const [chosenConsequence, setChosenConsequence] = useState(null);
  const [audioPlaying, setAudioPlaying] = useState(false);

  const currentScene = scenes.find((s) => s.id === currentSceneId);

  const narration =
    lang === 'tr'
      ? currentScene?.narration_tr
      : currentScene?.narration_en || currentScene?.narration_tr;

  const handleChoiceSelect = useCallback(
    (choice) => {
      const choicePoints = choice.points || 0;
      const newTotal = totalPoints + choicePoints;
      setTotalPoints(newTotal);
      play('click');

      const consequence =
        lang === 'tr'
          ? choice.consequence_tr
          : choice.consequence_en || choice.consequence_tr;
      setChosenConsequence(consequence || null);

      if (choice.next_scene_id) {
        const nextScene = scenes.find((s) => s.id === choice.next_scene_id);
        if (nextScene) {
          setSceneHistory((h) => [...h, choice.next_scene_id]);
          setTimeout(
            () => {
              setChosenConsequence(null);
              setCurrentSceneId(choice.next_scene_id);
            },
            shouldReduce ? 0 : 800
          );
        } else {
          // next_scene_id geçersiz — outcome ekranına geç
          handleOutcome(newTotal);
        }
      } else {
        // No next scene — go to outcome
        handleOutcome(newTotal);
      }
    },
    [totalPoints, lang, play, scenes, shouldReduce] // eslint-disable-line
  );

  const handleOutcome = useCallback(
    (_finalPoints) => {
      setShowOutcome(true);
      play('success');
      if (!shouldReduce) {
        fire({ particleCount: 80 });
      }
    },
    [play, shouldReduce, fire]
  );

  const handleFinish = useCallback(() => {
    const score = maxXP > 0 ? Math.round((totalPoints / 100) * maxXP) : totalPoints;
    onPhaseComplete({
      score: Math.min(score, maxXP),
      maxScore: maxXP,
      answers: { totalPoints, sceneHistory },
      timeSpent: 0,
    });
  }, [maxXP, totalPoints, sceneHistory, onPhaseComplete]);

  const handlePlayAudio = useCallback(() => {
    if (!currentScene?.audio_url || audioPlaying) return;
    const audio = new Audio(currentScene.audio_url);
    setAudioPlaying(true);
    audio.play().catch(() => {});
    audio.onended = () => setAudioPlaying(false);
  }, [currentScene, audioPlaying]);

  const continueNoChoices = useCallback(() => {
    play('click');
    handleOutcome(totalPoints);
  }, [play, handleOutcome, totalPoints]);

  // Outcome screen
  if (showOutcome) {
    const matchedOutcome = outcomes.find(
      (o) => totalPoints >= (o.min_points || 0) && totalPoints <= (o.max_points || Infinity)
    ) || outcomes[0];

    const outcomeTitle =
      lang === 'tr'
        ? matchedOutcome?.title_tr
        : matchedOutcome?.title_en || matchedOutcome?.title_tr;
    const outcomeDesc =
      lang === 'tr'
        ? matchedOutcome?.desc_tr
        : matchedOutcome?.desc_en || matchedOutcome?.desc_tr;

    return (
      <motion.div
        initial={shouldReduce ? {} : { opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="flex flex-col items-center gap-6 w-full max-w-2xl mx-auto text-center py-8"
        data-testid={`${testId}-outcome`}
      >
        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center shadow-[0_4px_0_0_#d97706]">
          <CheckCircle size={40} className="text-white" aria-hidden="true" />
        </div>
        <div>
          <h2 className="text-2xl font-black text-[var(--text-main)] mb-2">
            {outcomeTitle || t('cabin.exercise.storyMode.outcome')}
          </h2>
          {outcomeDesc && (
            <p className="text-sm text-[var(--text-muted)] max-w-md mx-auto">{outcomeDesc}</p>
          )}
        </div>
        <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl px-8 py-4">
          <p className="text-3xl font-black text-amber-600">{totalPoints}</p>
          <p className="text-sm text-amber-700 font-semibold mt-1">
            {t('cabin.exercise.storyMode.points')}
          </p>
        </div>
        <p className="text-xs text-[var(--text-muted)]">
          {t('cabin.exercise.storyMode.scenesVisited').replace('{n}', sceneHistory.length)}
        </p>
        <motion.button
          whileHover={shouldReduce ? {} : { scale: 1.02 }}
          whileTap={shouldReduce ? {} : { scale: 0.98 }}
          onClick={handleFinish}
          className="flex items-center gap-2 px-8 py-3 bg-[var(--brand-primary)] text-white font-bold rounded-xl shadow-[0_4px_0_0_var(--brand-primary-shadow)] hover:bg-[var(--brand-primary-hover)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
          data-testid={`${testId}-finish`}
        >
          {t('cabin.exercise.storyMode.finish')}
          <ChevronRight size={18} aria-hidden="true" />
        </motion.button>
      </motion.div>
    );
  }

  if (!currentScene) return null;

  const choices = currentScene.choices || [];
  const sceneIndex = sceneHistory.length;

  return (
    <div
      className="flex flex-col gap-5 w-full max-w-2xl mx-auto"
      data-testid={testId}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center">
            <BookOpen size={18} className="text-amber-600" aria-hidden="true" />
          </div>
          <span className="text-sm font-bold text-[var(--text-muted)]">
            {t('cabin.exercise.storyMode.scene').replace('{n}', sceneIndex)}
          </span>
        </div>
        {maxXP > 0 && (
          <span className="text-xs font-semibold text-amber-500 bg-amber-50 border border-amber-200 rounded-full px-3 py-1">
            {totalPoints} pts
          </span>
        )}
      </div>

      {/* Scene card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSceneId}
          initial={shouldReduce ? {} : { opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={shouldReduce ? {} : { opacity: 0, x: -24 }}
          transition={{ duration: 0.2 }}
          className="bg-white border-2 border-[var(--border-light)] rounded-2xl overflow-hidden shadow-[0_4px_0_0_rgba(0,0,0,0.06)]"
        >
          {/* Image */}
          {currentScene.image_url && (
            <div className="h-36 bg-gradient-to-br from-amber-50 to-sky-50 flex items-center justify-center overflow-hidden">
              <img
                src={currentScene.image_url}
                alt=""
                aria-hidden="true"
                className="max-h-32 object-contain"
              />
            </div>
          )}

          <div className="p-5">
            {/* Speaker badge */}
            {currentScene.speaker && (
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-full bg-sky-100 flex items-center justify-center shrink-0">
                  {currentScene.speaker_avatar ? (
                    <img
                      src={currentScene.speaker_avatar}
                      alt={currentScene.speaker}
                      className="w-7 h-7 rounded-full object-cover"
                    />
                  ) : (
                    <User size={14} className="text-sky-600" aria-hidden="true" />
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <MessageCircle size={12} className="text-sky-500" aria-hidden="true" />
                  <span className="text-xs font-bold text-sky-600">{currentScene.speaker}</span>
                </div>
              </div>
            )}

            {/* Narration */}
            <p className="text-base text-[var(--text-main)] leading-relaxed font-medium">
              {narration}
            </p>

            {/* Audio */}
            {currentScene.audio_url && (
              <button
                onClick={handlePlayAudio}
                disabled={audioPlaying}
                aria-label={t('cabin.tts.play')}
                className="mt-3 flex items-center gap-1.5 text-xs text-sky-600 hover:text-sky-700 font-semibold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] disabled:opacity-50"
              >
                <Volume2 size={14} aria-hidden="true" />
                {audioPlaying
                  ? t('cabin.tts.pause')
                  : t('cabin.exercise.storyMode.playAudio')}
              </button>
            )}

            {/* Consequence feedback */}
            <AnimatePresence>
              {chosenConsequence && (
                <motion.div
                  initial={shouldReduce ? {} : { opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="mt-3 p-3 bg-sky-50 border border-sky-200 rounded-xl text-sm text-sky-700 font-medium"
                  role="status"
                >
                  {chosenConsequence}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Choices */}
      {choices.length > 0 ? (
        <div
          className="flex flex-col gap-2"
          role="group"
          aria-label={t('cabin.exercise.storyMode.choose')}
        >
          <p className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wide">
            {t('cabin.exercise.storyMode.choose')}
          </p>
          {choices.map((choice, idx) => {
            const choiceText =
              lang === 'tr' ? choice.text_tr : choice.text_en || choice.text_tr;
            return (
              <motion.button
                key={idx}
                whileHover={shouldReduce ? {} : { scale: 1.01 }}
                whileTap={shouldReduce ? {} : { scale: 0.99 }}
                onClick={() => handleChoiceSelect(choice)}
                disabled={!!chosenConsequence}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 border-2 border-[var(--border-light)] bg-white rounded-xl text-sm font-semibold text-[var(--text-main)] hover:border-[var(--brand-primary)] hover:bg-sky-50 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] disabled:opacity-40 disabled:cursor-wait text-left"
                data-testid={`${testId}-choice-${idx}`}
              >
                <span>{choiceText}</span>
                {choice.points > 0 && (
                  <span className="text-xs text-amber-500 font-bold shrink-0">
                    +{choice.points}
                  </span>
                )}
              </motion.button>
            );
          })}
        </div>
      ) : (
        <motion.button
          whileHover={shouldReduce ? {} : { scale: 1.01 }}
          whileTap={shouldReduce ? {} : { scale: 0.99 }}
          onClick={continueNoChoices}
          className="self-end flex items-center gap-2 px-6 py-3 bg-[var(--brand-primary)] text-white font-bold rounded-xl shadow-[0_4px_0_0_var(--brand-primary-shadow)] hover:bg-[var(--brand-primary-hover)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
          data-testid={`${testId}-continue`}
        >
          {t('cabin.exercise.storyMode.next')}
          <ChevronRight size={18} aria-hidden="true" />
        </motion.button>
      )}
    </div>
  );
}
